/**
 * The support page's content — the page App Store Connect's support URL lands
 * on (guideline 1.5: a reviewer follows it and expects a real route in), and
 * where Settings › About › Support in the app opens.
 *
 * Pure of the router and of `import.meta.env`: the contact is passed in — the
 * same `VITE_REMOVAL_CONTACT` the signup notice and the policy publish, so the
 * site cannot name two different addresses. With no contact configured the
 * page still reads as finished: it points at the listing's contact route
 * rather than trailing off into a blank. The privacy policy link is the
 * footer's (rendered by the route), so this block needs no router to render
 * or to test.
 *
 * The address is a real `<a href="mailto:">` (the site-header precedent for a
 * true anchor): focusable, announced as a link, and it opens the mail client.
 */

import type { CSSProperties } from "react";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { PageSection } from "@/components/page-section";
import { headingProps } from "@/lib/heading-props";
import { useAppTheme } from "@/theme/paper-theme";
import { radius, spacing } from "@/theme/tokens";

const PAGE_HEADING_PROPS = headingProps(1);

const CONTACT_LINK_STYLE: CSSProperties = {
  borderRadius: radius.sm,
  padding: spacing.xxs,
};

export function SupportContent({ contact }: { contact?: string }) {
  const theme = useAppTheme();
  const address = contact?.trim() ?? "";

  return (
    <>
      <View style={styles.intro}>
        <Text variant="headlineLarge" {...PAGE_HEADING_PROPS}>
          Support
        </Text>
        <Text variant="bodyLarge">
          Sparkie is made by one person, and email is the support channel. Most
          questions are answered within a couple of days.
        </Text>
      </View>

      <PageSection title="Get in touch">
        {address.length > 0 ? (
          <Text variant="bodyMedium">
            Email{" "}
            <a
              href={`mailto:${address}`}
              style={{ ...CONTACT_LINK_STYLE, color: theme.colors.primary }}
            >
              {address}
            </a>{" "}
            and say what you were doing when something went wrong.
          </Text>
        ) : (
          <Text variant="bodyMedium">
            Support requests go through the contact route on the app's App Store
            listing. Say what you were doing when something went wrong.
          </Text>
        )}
        <Text variant="bodyMedium">
          It helps to include your iPhone model, its iOS version, and the app
          version shown in Settings › About inside Sparkie.
        </Text>
      </PageSection>

      <PageSection title="Subscriptions">
        <Text variant="bodyMedium">
          Sparkie Premium is billed by Apple, so changing plan, cancelling, or
          asking for a refund all happen with Apple: open Settings › Apple ID ›
          Subscriptions on your iPhone, or use Apple's Report a Problem page.
          Sparkie never sees your payment details and cannot change a
          subscription for you. Settings › Subscription › Restore Purchases in
          the app re-syncs a subscription bought on the same Apple ID.
        </Text>
      </PageSection>

      <PageSection title="Your account and your notes">
        <Text variant="bodyMedium">
          Your notes are files on your iPhone, in Documents/Sparkie, and nothing
          about your account touches them. Delete the account at any time from
          Settings › Account › Delete account in the app: it removes your email
          and sign-in from our servers right away, leaves every note on the
          device, and does not cancel a subscription — that stays with Apple.
        </Text>
        <Text variant="bodyMedium">
          What the app collects and why is on the privacy policy, linked below.
        </Text>
      </PageSection>
    </>
  );
}

const styles = StyleSheet.create({
  intro: {
    gap: spacing.sm,
  },
});
